/* eslint-disable max-lines-per-function, max-lines,  max-depth */
import { AgGridAngular } from '@ag-grid-community/angular';
import {
  CellClassParams,
  CellEditingStoppedEvent,
  CellValueChangedEvent,
  ColDef,
  ColGroupDef,
  EditableCallbackParams,
  GetRowIdFunc,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ITooltipParams,
  ValueFormatterParams,
  ValueGetterParams,
} from '@ag-grid-community/core';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  effect,
  EventEmitter,
  inject,
  Input,
  input,
  model,
  OnDestroy,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { cloneDeep, isEqual, set } from 'lodash-es';
import { BehaviorSubject, firstValueFrom } from 'rxjs';

import { UnitsOfMeasureDropdownComponent } from '@features/inline-budget/be-activities-attributes-modal/activities-tab/components/units-of-measure-dropdown/units-of-measure-dropdown.component';
import { ActivitiesTabService } from '@features/inline-budget/be-activities-attributes-modal/activities-tab/services/activities-tab.services';

import { AgRequiredHeaderComponent } from '@shared/ag-components/ag-required-header/ag-required-header.component';
import { extendableOptionsDropdownRenderer } from '@shared/components/ag-raw-options-dropdown-renderer/ag-raw-options-dropdown-renderer';
import {
  ConfirmationActionModalComponent,
  ConfirmationActionModalData,
} from '@shared/components/modals/confirmation-action-modal/confirmation-action-modal.components';
import { TableConstants } from '@shared/constants/table.constants';
import { ActivitySubType, ActivityType, Currency } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { GlobalStore } from '@shared/store/global/global.store';
import { decimalDivide } from '@shared/utils';
import { agFormatToNumberValueSetter } from '@shared/utils/ag-value-setter.utils';
import { getEncodedAttributeName } from '@shared/utils/get-encoded-attribute-name';
import { Utils } from '@shared/utils/utils';

import { AgDeleteCellComponent, AgDeleteCellComponentParams } from '../../ag-delete-cell.component';
import { AgTooltipComponent } from '../../ag-tooltip.component';
import { BeInlineCategoryDropdownComponent } from '../../be-inline-category-dropdown/be-inline-category-dropdown.component';
import {
  BeInlineCategoryDropdownOption,
  BeInlineCategoryDropdownParams,
} from '../../be-inline-category-dropdown/be-inline-category-dropdown.model';
import {
  BeActivitiesAttributesModalParams,
  BeActivitiesAttributesModalRowData,
  BeActivitiesTabState,
  BeActivitiesTabToSave,
  BeActivityTypes,
  InAppDocumentState,
} from '../be-activities-attributes-modal.model';

const EMPTY_TABLE_TOOLTIP =
  'To continue, include an Activity with Units, Unit Cost, and location in budget';
const ERROR_TABLE_TOOLTIP = 'Please resolve errors to continue';
const PIVOT_ATTRIBUTE_ROW_ERROR =
  'Attributes used in JE Configuration must be included for all activities.';

@Component({
  imports: [NgSelectModule, FormsModule, AgGridAngular, CommonModule],
  selector: 'aux-activities-tab',
  styleUrls: ['activities-tab.component.scss'],
  templateUrl: 'activities-tab.component.html',
})
export class ActivitiesTabComponent implements OnDestroy, OnInit {
  activitiesTabService = inject(ActivitiesTabService);
  globalStore = inject(GlobalStore);

  private cdr = inject(ChangeDetectorRef);
  private overlayService = inject(OverlayService);

  editButtonDisabled = signal(false);
  saveButtonDisabled = signal(true);

  inAppDocumentState = model<InAppDocumentState>({ bucket_keys: [], notes: '' });

  showNotesSection = input.required<boolean>();

  categories: BeInlineCategoryDropdownOption[] = [];
  categories$ = new BehaviorSubject<BeInlineCategoryDropdownOption[]>([]);
  @Output() closeTab = new EventEmitter<void>();
  columns: (ColDef | ColGroupDef)[] = [];
  currencyName = '';
  @Input() data: BeActivitiesAttributesModalParams | null = null;
  deletedRows: Record<string, BeActivitiesAttributesModalRowData> = {};
  editButtonTooltip = EMPTY_TABLE_TOOLTIP;
  filteredRowData: BeActivitiesAttributesModalRowData[] = [];
  firstDataLoad = true;
  gridAPI!: GridApi;
  @Input() gridData$ = new BehaviorSubject<BeActivitiesAttributesModalRowData[]>([]);
  gridOptions: GridOptions = {};
  @Input() hasExternalChanges = false;
  headerNames: Record<string, string> = {};
  initialTableEmpty = false;
  invalidRows: Record<string, BeActivitiesAttributesModalRowData> = {};
  isCategoryListChanged = new BehaviorSubject<boolean>(false);
  isThereAnyNotes = new BehaviorSubject<boolean>(false);
  rowData: BeActivitiesAttributesModalRowData[] = [];
  @Output() save = new EventEmitter<BeActivitiesTabToSave>();
  saveButtonTooltip = EMPTY_TABLE_TOOLTIP;
  saveButtonTooltipForNotes = 'Enter required note to save.';
  selectedCategories = {} as Record<BeActivityTypes, boolean>;
  selectedVendor = '';
  @Output() toggleTabs = new EventEmitter<BeActivitiesTabState>();
  validRows: Record<string, BeActivitiesAttributesModalRowData> = {};
  vendors: { id: string; label: string }[] = [];

  ngOnInit(): void {
    if (this.data) {
      this.categories = cloneDeep(this.data.categories);
      this.categories$.next(this.categories);
      this.currencyName = this.data.currencyName;
      this.editButtonDisabled.set(!this.data.editMode);
      this.vendors = [this.data.vendor];
      this.selectedVendor = this.data.vendor.id;

      this.columns = [
        {
          cellClass: '!p-0 h-full',
          cellRenderer: AgDeleteCellComponent,
          cellRendererParams: <AgDeleteCellComponentParams>{
            isDisabled: (params: AgDeleteCellComponentParams) => {
              return params.data?.deletable
                ? ''
                : 'Activities with actuals booked or invoices mapped cannot be deleted.';
            },
            onDelete: async (params: AgDeleteCellComponentParams) => {
              const data = params.node.data;

              if (!data) {
                return;
              }

              if (!data.isGenerated) {
                const ref = this.overlayService.openPopup<
                  ConfirmationActionModalData,
                  boolean,
                  ConfirmationActionModalComponent
                >({
                  data: {
                    keywordToExecuteAction: 'Delete Activity',
                    message:
                      'This will permanently delete the selected activity. This action cannot be undone, and аll associated data cannot be recovered.',
                  },
                  modal: ConfirmationActionModalComponent,
                  settings: {
                    header: 'Delete Activity?',
                    primaryButton: {
                      label: 'Delete Activity',
                      variant: 'negative',
                    },
                  },
                });

                const { data: refData } = await firstValueFrom(ref.afterClosed$);

                if (!refData) {
                  return;
                }

                this.deletedRows[data.id] = data;
              }

              delete this.validRows[data.id];
              delete this.invalidRows[data.id];
              switch (data.activity_type) {
                case ActivitySubType.ACTIVITY_INVESTIGATOR_PATIENT_INVOICEABLES:
                case ActivitySubType.ACTIVITY_INVESTIGATOR_PATIENT_VISITS:
                case ActivitySubType.ACTIVITY_INVESTIGATOR_SITE_INVOICEABLES:
                case ActivityType.ACTIVITY_DISCOUNT:
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  delete this.selectedCategories[data.activity_type];
                  break;
                default:
                  break;
              }

              this.gridAPI.applyTransaction({
                remove: [{ id: data.id }],
              });
              this.updateState();
              this.cdr.markForCheck();
            },
          },
          editable: false,
          field: 'action',
          headerClass: 'two-row-header ag-header-align-center',
          headerName: '',
          maxWidth: 32,
          minWidth: 32,
          width: 32,
        },
        {
          cellClass: '!p-0',
          cellClassRules: {
            'error-outline': (params: CellClassParams<BeActivitiesAttributesModalRowData>) =>
              (params.data?.isGenerated ? !!this.invalidRows[params.data.id] : true) &&
              !params.value,
          },
          cellRenderer: BeInlineCategoryDropdownComponent,
          cellRendererParams: <BeInlineCategoryDropdownParams>{
            categories$: this.categories$,
            onCategoryAdd: (newCategoryName: string, option: BeInlineCategoryDropdownOption) => {
              const newCategory: BeInlineCategoryDropdownOption = {
                categoryType: option.categoryType,
                disabled: false,
                fullPath:
                  (option.indent === 1 ? '' : option.path + '>' + option.name) +
                  '>' +
                  newCategoryName,
                id: Utils.uuid(),
                indent: option.indent + 1,
                name: newCategoryName,
                path: option.indent === 1 ? '' : option.path + '>' + option.name,
                type: option.type,
              };
              const optionIndex = this.categories.findIndex(
                (item) => item.id === option.id && item.name === option.name
              );
              const index = this.categories.findIndex(
                (item, index) => item.indent <= option.indent && index > optionIndex,
                optionIndex
              );

              if (index !== -1) {
                this.categories.splice(index, 0, newCategory);
              } else {
                this.categories.push(newCategory);
              }

              this.isCategoryListChanged.next(true);
              this.categories$.next(this.categories);

              return newCategory;
            },
            onCategoryChange: (params, id, type) => {
              const data = params.data;

              if (!data) return;

              const updateData = <BeActivitiesAttributesModalRowData>{ ...params.node.data };

              const activityMap = {
                [ActivitySubType.ACTIVITY_INVESTIGATOR_PATIENT_INVOICEABLES]:
                  'Patient Invoiceables',
                [ActivitySubType.ACTIVITY_INVESTIGATOR_PATIENT_VISITS]: 'Patient Visits',
                [ActivitySubType.ACTIVITY_INVESTIGATOR_SITE_INVOICEABLES]: 'Site Invoiceables',
                [ActivityType.ACTIVITY_DISCOUNT]: 'Services Discount',
              } as const;

              if (type in activityMap) {
                const selectedActivityName = activityMap[type as keyof typeof activityMap];

                updateData.activity_name = selectedActivityName;
                data.activity_name = selectedActivityName;

                if (type === ActivityType.ACTIVITY_DISCOUNT) {
                  updateData.unit_num = 1;
                  data.unit_num = 1;
                }

                this.selectedCategories[type] = true;

                params.node.updateData(updateData);
              }

              this.updateState({ ...data, activity_type: type, category: id });
              this.gridAPI.refreshCells({ rowNodes: [params.node] });
            },
            onCategoryRename: (newCategoryName: string, option: BeInlineCategoryDropdownOption) => {
              const oldFullPath = option.fullPath;
              const optionIndex = this.categories.findIndex(
                (item) => item.id === option.id && item.name === option.name
              );

              this.categories[optionIndex].name = newCategoryName;
              this.categories[optionIndex].fullPath =
                `${this.categories[optionIndex].path}>${this.categories[optionIndex].name}`;
              this.categories[optionIndex].isRenamed = true;
              this.categories = this.categories.map((category) => {
                if (category.path.startsWith(oldFullPath) && category.type === option.type) {
                  return {
                    ...category,
                    fullPath: category.fullPath.replace(
                      oldFullPath,
                      this.categories[optionIndex].fullPath.replace(/\$/g, '$$$$')
                    ),
                    isRenamed: true,
                    path: category.path.replace(
                      oldFullPath,
                      this.categories[optionIndex].fullPath.replace(/\$/g, '$$$$')
                    ),
                  };
                }

                return category;
              });

              this.categories$.next(this.categories);
              this.isCategoryListChanged.next(true);
            },
            usedCategories: () => {
              const ct = {
                ...this.selectedCategories,
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                ...Object.entries(this.data.usedCategories).reduce(
                  (acc, [key, bool]) => {
                    if (bool) {
                      acc[key as BeActivityTypes] = true;
                    }

                    return acc;
                  },
                  {} as Record<BeActivityTypes, boolean>
                ),
              };

              return {
                ...ct,
                [ActivityType.ACTIVITY_INVESTIGATOR]:
                  ct.ACTIVITY_INVESTIGATOR_PATIENT_INVOICEABLES &&
                  ct.ACTIVITY_INVESTIGATOR_PATIENT_VISITS &&
                  ct.ACTIVITY_INVESTIGATOR_SITE_INVOICEABLES,
              };
            },
          },
          editable: false,
          field: 'category',
          headerClass: 'two-row-header ag-header-align-center',
          headerComponent: AgRequiredHeaderComponent,
          headerName: 'Category',
          minWidth: 250,
        },
        {
          cellClass: '!block truncate text-left',
          cellClassRules: {
            'error-outline': (params: CellClassParams<BeActivitiesAttributesModalRowData>) =>
              (params.data?.isGenerated ? !!this.invalidRows[params.data.id] : true) &&
              (!params.value || !params.value.trim()),
          },
          editable: (params: EditableCallbackParams<BeActivitiesAttributesModalRowData>) => {
            switch (params.data?.activity_type) {
              case ActivitySubType.ACTIVITY_INVESTIGATOR_PATIENT_INVOICEABLES:
              case ActivitySubType.ACTIVITY_INVESTIGATOR_PATIENT_VISITS:
              case ActivitySubType.ACTIVITY_INVESTIGATOR_SITE_INVOICEABLES:
              case ActivityType.ACTIVITY_DISCOUNT:
              case ActivityType.ACTIVITY_INVESTIGATOR:
                return false;
              default:
                return true;
            }
          },
          field: 'activity_name',
          headerClass: 'two-row-header ag-header-align-center',
          headerComponent: AgRequiredHeaderComponent,
          headerName: 'Activities',
          tooltipValueGetter: (params: ITooltipParams<BeActivitiesAttributesModalRowData>) => {
            if (params.data) {
              switch (params.data.activity_type) {
                case ActivitySubType.ACTIVITY_INVESTIGATOR_PATIENT_INVOICEABLES:
                case ActivitySubType.ACTIVITY_INVESTIGATOR_PATIENT_VISITS:
                case ActivitySubType.ACTIVITY_INVESTIGATOR_SITE_INVOICEABLES:
                case ActivityType.ACTIVITY_DISCOUNT:
                case ActivityType.ACTIVITY_INVESTIGATOR:
                  return 'Auxilius Product restricts Category and Activity Name for Investigator and Discount.';
                default:
                  break;
              }

              return params.data.activity_name;
            }

            return '';
          },
        },
        {
          children: [
            {
              field: 'display_label',
              headerClass: 'ag-header-align-center text-sm',
              headerName: 'Display Label',
              maxWidth: 120,
              width: 120,
            },
            {
              cellClass: ['!block', 'truncate', 'text-left', '!p-0'],
              cellEditor: UnitsOfMeasureDropdownComponent,
              cellRenderer: extendableOptionsDropdownRenderer,
              colId: 'uom',
              editable: (params: EditableCallbackParams<BeActivitiesAttributesModalRowData>) => {
                return !params.data?.isPercentage;
              },
              field: 'uom',
              headerClass: 'ag-header-align-center text-sm',
              headerName: 'Unit of Measure',
              hide: this.data.uomHide,
              maxWidth: 180,
              tooltipValueGetter: (params: ITooltipParams<BeActivitiesAttributesModalRowData>) => {
                if (params.data?.isPercentage) {
                  return 'Discount Percentage cannot be updated. Please contact Auxilius if needed.';
                }

                return params.data?.uom || '';
              },
              valueFormatter: (params) => params.value || '',
              width: 180,
            },
            {
              cellClass: ['budget-units', 'text-right'],
              cellClassRules: {
                'error-outline': (params: CellClassParams<BeActivitiesAttributesModalRowData>) =>
                  (params.data?.isGenerated ? !!this.invalidRows[params.data.id] : true) &&
                  !this.isValidNumber(params.value),
              },
              editable: (params: EditableCallbackParams<BeActivitiesAttributesModalRowData>) => {
                switch (params.data?.activity_type) {
                  case ActivityType.ACTIVITY_DISCOUNT:
                    return false;
                  default:
                    return true;
                }
              },
              field: 'unit_num',
              headerClass: 'ag-header-align-center text-sm',
              headerComponent: AgRequiredHeaderComponent,
              headerName: 'Units',
              maxWidth: 120,
              tooltipValueGetter: (params: ITooltipParams<BeActivitiesAttributesModalRowData>) => {
                if (params.data?.isPercentage) {
                  return 'Discount Percentage cannot be updated. Please contact Auxilius if needed.';
                }

                return '';
              },
              valueFormatter: (params) => {
                const resp = Utils.decimalFormatter(params.data?.unit_num);

                return resp === Utils.zeroHyphen && this.isValidNumber(params.value)
                  ? `${params.value}`
                  : resp;
              },
              valueParser: (p) => {
                if (p.newValue == '') {
                  return '';
                }

                const parsed = parseFloat(p.newValue);

                return Number.isFinite(parsed) ? parsed : p.newValue;
              },
              valueSetter: (params) => {
                params.data.unit_num = params.newValue;

                return true;
              },
              width: 120,
            },
            {
              cellClass: (p) => [`budgetCost${p.data.currencyName}`, 'text-right'],
              cellClassRules: {
                'error-outline': (params: CellClassParams<BeActivitiesAttributesModalRowData>) => {
                  const data = params.data;

                  if (data) {
                    if (data.isPercentage) {
                      return false;
                    }

                    const isDiscountInvalid =
                      data.activity_type === ActivityType.ACTIVITY_DISCOUNT
                        ? +params.value >= 0
                        : false;

                    return (
                      (data.isGenerated && !this.isRowEmpty(data)
                        ? !this.isValidNumber(params.value)
                        : false) ||
                      (this.isRowEmpty(data) ? false : !this.isValidNumber(params.value)) ||
                      isDiscountInvalid
                    );
                  }

                  return false;
                },
              },
              cellDataType: 'text',
              editable: (params: EditableCallbackParams<BeActivitiesAttributesModalRowData>) => {
                return !params.data?.isPercentage;
              },
              field: 'unit_cost',
              headerClass: 'ag-header-align-center text-sm',
              headerComponent: AgRequiredHeaderComponent,
              headerName: 'Unit Cost',
              maxWidth: 120,
              tooltipValueGetter: (params: ITooltipParams<BeActivitiesAttributesModalRowData>) => {
                if (params.data?.isPercentage) {
                  return 'Discount Percentage cannot be updated. Please contact Auxilius if needed.';
                }

                return '';
              },
              valueFormatter: (
                params: ValueFormatterParams<BeActivitiesAttributesModalRowData>
              ) => {
                if (params.data) {
                  if (params.data.isPercentage) {
                    return Utils.percentageFormatter(decimalDivide(params.value, 100), {
                      maximumFractionDigits: 2,
                      minimumFractionDigits: 2,
                    });
                  }

                  const resp = Utils.agCurrencyFormatter(
                    params,
                    params.data.currencyName as Currency
                  );

                  return resp === Utils.zeroHyphen && this.isValidNumber(params.value)
                    ? `${params.value}`
                    : resp;
                }

                return '';
              },
              valueSetter: (params) =>
                params.newValue === '' || params.newValue == null
                  ? set(params.data, 'unit_cost', '')
                  : agFormatToNumberValueSetter(params, ['$', ','], 'unit_cost'),
              width: 120,
            },
            {
              cellClass: (p) => [
                `budgetCost${p.data.currencyName}`,
                'text-right',
                TableConstants.STYLE_CLASSES.NOT_EDITABLE_CELL,
              ],
              cellClassRules: {
                'error-outline': (params: CellClassParams<BeActivitiesAttributesModalRowData>) => {
                  const data = params.data;

                  if (data) {
                    if (data.isPercentage) {
                      return false;
                    }

                    const isDiscountInvalid =
                      data.activity_type === ActivityType.ACTIVITY_DISCOUNT
                        ? +params.value >= 0
                        : false;

                    return (
                      (data.isGenerated && !this.isRowEmpty(data)
                        ? !this.isValidNumber(params.value)
                        : false) ||
                      (this.isRowEmpty(data) ? false : !this.isValidNumber(params.value)) ||
                      isDiscountInvalid
                    );
                  }

                  return false;
                },
              },
              cellStyle: {
                'text-overflow': 'unset',
              },
              editable: false,
              headerClass: 'ag-header-align-center text-sm',
              headerComponent: AgRequiredHeaderComponent,
              headerName: 'Total Cost',
              maxWidth: 120,
              valueFormatter: (
                params: ValueFormatterParams<BeActivitiesAttributesModalRowData>
              ) => {
                if (params.data) {
                  if (params.data.isPercentage) {
                    return Utils.percentageFormatter(decimalDivide(params.value, 100), {
                      maximumFractionDigits: 2,
                      minimumFractionDigits: 2,
                    });
                  }

                  const resp = Utils.agCurrencyFormatter(
                    params,
                    params.data.currencyName as Currency
                  );

                  return resp === Utils.zeroHyphen && this.isValidNumber(params.value)
                    ? `${params.value}`
                    : resp;
                }

                return '';
              },
              valueGetter: (params: ValueGetterParams<BeActivitiesAttributesModalRowData>) => {
                let val;

                if (params.data) {
                  if (
                    this.isValidNumber(params.data.unit_cost) &&
                    this.isValidNumber(params.data.unit_num)
                  ) {
                    val = params.data.unit_num * params.data.unit_cost;
                  }
                }

                return val ?? '';
              },
              width: 120,
            },
          ],
          headerClass:
            'ag-header-align-center bg-aux-gray-dark gray-dark-header-group aux-black border-0',
          headerName: 'Overall Budget',
        },
      ];

      this.data.attributes.forEach((attr) => {
        const field = `attributes.${getEncodedAttributeName(attr.attribute_name)}`;

        this.headerNames[field] = attr.attribute_name;
      });

      this.gridOptions = <GridOptions>{
        ...TableConstants.DEFAULT_GRID_OPTIONS.EDIT_GRID_OPTIONS,
        columnDefs: this.columns,
        defaultColDef: {
          editable: true,
          suppressMenu: true,
          tooltipComponent: AgTooltipComponent,
        },
        enableFillHandle: true,
        enableRangeSelection: true,
        fillOperation: (params) => {
          let index = params.rowNode.rowIndex;

          if (params.column.getColId() === 'category' && index != null) {
            switch (params.direction) {
              case 'down':
                index -= 1;
                break;
              case 'left':
              case 'right':
                return false;
              case 'up':
                index += 1;
                break;
            }

            const firstFocusedRowIndex = params.api.getFocusedCell()?.rowIndex;

            if (
              index < params.api.getDisplayedRowCount() - 1 &&
              firstFocusedRowIndex !== undefined
            ) {
              const starterNode = params.api.getDisplayedRowAtIndex(firstFocusedRowIndex);
              const changeNode =
                params.direction === 'down'
                  ? params.api.getDisplayedRowAtIndex(index + 1)
                  : params.api.getDisplayedRowAtIndex(index - 1);
              const passedActivityTypes = [
                ActivityType.ACTIVITY_DISCOUNT,
                ActivityType.ACTIVITY_INVESTIGATOR,
                ActivitySubType.ACTIVITY_INVESTIGATOR_PATIENT_VISITS,
                ActivitySubType.ACTIVITY_INVESTIGATOR_SITE_INVOICEABLES,
                ActivitySubType.ACTIVITY_INVESTIGATOR_PATIENT_INVOICEABLES,
              ];

              if (changeNode && starterNode) {
                const currentNodeActivityType = changeNode.data.activity_type as
                  | ActivitySubType
                  | ActivityType;
                const starterNodeActivityType = starterNode.data.activity_type as
                  | ActivitySubType
                  | ActivityType;

                if (
                  currentNodeActivityType &&
                  starterNodeActivityType &&
                  currentNodeActivityType !== starterNodeActivityType
                ) {
                  return false;
                }

                if (changeNode.data.category) {
                  if (passedActivityTypes.includes(currentNodeActivityType)) {
                    return false;
                  }
                } else {
                  if (passedActivityTypes.includes(starterNodeActivityType)) {
                    return false;
                  }
                }
              }

              const data = {
                ...params.rowNode.data,
                activity_type: starterNode
                  ? starterNode.data.activity_type
                  : params.rowNode.data.activity_type,
                category: starterNode ? starterNode.data.category : params.rowNode.data.category,
              };

              params.rowNode.updateData(data);
              this.updateState(data);
              this.gridAPI.refreshCells({ rowNodes: [params.rowNode] });
            }
          }

          return false;
        },
        headerHeight: 32,
        onBodyScroll: (event) => {
          const dropdown = event.api.getCellEditorInstances()[0];

          if (dropdown && dropdown instanceof UnitsOfMeasureDropdownComponent) {
            dropdown.close();
          }
        },
        stopEditingWhenCellsLoseFocus: false,
        tooltipShowDelay: 0,
      };
    }

    this.gridData$.subscribe((value) => {
      this.rowData = value;

      // if the rows are empty or less than 5, add empty rows
      if (this.rowData.length < 6 && !this.data?.editMode) {
        const rowsToAdd = 5 - this.rowData.length;

        for (let i = 0; i < rowsToAdd; i++) {
          this.rowData.push(this.emptyRow());
        }
      }

      if (this.firstDataLoad) {
        this.initialTableEmpty = this.areAllRowsEmpty(this.rowData);
      }

      this.rowData.forEach((row) => {
        if (row.changed) {
          this.updateState(row);
        }
      });

      this.filteredRowData = this.rowData.filter((row) => {
        if (row.deleted) {
          this.deletedRows[row.id] = row;

          return false;
        }

        return true;
      });
    });
  }

  ngOnDestroy(): void {
    document.removeEventListener('click', this.onClickOutsideTable);
  }

  addAttributesIfDoesntExist = (row: BeActivitiesAttributesModalRowData) => {
    Object.keys(this.headerNames).forEach((value) => {
      const v = value.replace('attributes.', '');

      if (!row.attributes[v]) {
        row.attributes[v] = '';
      }
    });

    return row;
  };

  addNewRow() {
    const nodeTransaction = this.gridAPI.applyTransaction({
      add: [{ ...this.emptyRow(), shouldOpenByDefault: true }],
    });

    this.gridAPI.sizeColumnsToFit();
    this.gridAPI.ensureIndexVisible(this.gridAPI.getRenderedNodes().length - 1, 'bottom');

    if (nodeTransaction?.add.length && nodeTransaction.add[0].rowIndex) {
      this.gridAPI.startEditingCell({
        colKey: 'category',
        rowIndex: nodeTransaction.add[0].rowIndex,
      });
    }
  }

  areAllRowsEmpty(data: BeActivitiesAttributesModalRowData[]) {
    this.firstDataLoad = false;

    return data.every((row) => !this.isRowValid(row));
  }

  constructor() {
    effect(() => {
      if (this.inAppDocumentState()) {
        this.setNotes();
      }
    });

    this.activitiesTabService.fetchUnitsOfMeasure().catch(console.error);
  }

  emptyRow = () => {
    return this.addAttributesIfDoesntExist(<BeActivitiesAttributesModalRowData>{
      activity_name: '',
      activity_no: '',
      activity_type: '',
      actualsToDateExists: false,
      attributes: {},
      category: '',
      changed: false,
      currencyName: this.currencyName,
      deletable: true,
      deleted: false,
      display_label: '',
      id: Utils.uuid(),
      isGenerated: true,
      isPercentage: false,
      unit_cost: '',
      unit_num: '',
      uom: '',
      vendor_id: this.selectedVendor || '',
    });
  };

  getRowData() {
    const ids = new Set(Object.keys(this.validRows));
    const deletedIds = new Set(Object.keys(this.deletedRows));

    const rows: BeActivitiesAttributesModalRowData[] = [];

    this.filteredRowData.forEach((row) => {
      if (!row.isGenerated || (row.isGenerated && row.changed)) {
        const changed = !!this.validRows[row.id];
        const deleted = !!this.deletedRows[row.id];

        ids.delete(row.id);
        deletedIds.delete(row.id);
        rows.push(
          this.addAttributesIfDoesntExist({
            ...row,
            ...this.validRows[row.id],
            changed,
            deleted,
          })
        );
      }
    });
    ids.forEach((id) => {
      rows.push(
        this.addAttributesIfDoesntExist({
          ...this.validRows[id],
          changed: true,
        })
      );
    });
    deletedIds.forEach((id) => {
      rows.push({
        ...this.deletedRows[id],
        deleted: true,
      });
    });

    return rows;
  }

  getRowId: GetRowIdFunc<BeActivitiesAttributesModalRowData> = (d) => {
    return d.data.id;
  };

  goToAttributesTab(): void {
    this.gridData$.next(this.getRowData());
    this.toggleTabs.emit({
      categories: this.categories,
      isCategoryListChanged: this.isCategoryListChanged.getValue(),
    });
  }

  ifAnyRowHasError() {
    const primary = this.globalStore.primaryAttribute()?.attribute_name;
    const secondary = this.globalStore.secondaryAttribute()?.attribute_name;

    if (!primary) {
      return false;
    }

    const primaryField = `${getEncodedAttributeName(primary)}`;
    const secondaryField = secondary ? `${getEncodedAttributeName(secondary)}` : '';

    const rows = this.getRowData().filter((row) => !row.deleted);

    for (const row of rows) {
      if (!row.attributes[primaryField]) {
        return true;
      }

      if (secondaryField && !row.attributes[secondaryField]) {
        return true;
      }
    }

    return false;
  }

  isActivitiesTabHasChanges(): boolean {
    return (
      !!Object.keys(this.invalidRows).length ||
      !!Object.keys(this.validRows).length ||
      !!Object.keys(this.deletedRows).length ||
      this.isCategoryListChanged.getValue()
    );
  }

  isRowEmpty(data: BeActivitiesAttributesModalRowData) {
    return (
      isEqual({ ...data, id: '', isGenerated: true }, { ...this.emptyRow(), id: '' }) ||
      isEqual(
        { ...data, id: '', isGenerated: true },
        { ...this.emptyRow(), id: '', shouldOpenByDefault: true }
      ) ||
      isEqual(
        { ...data, id: '', isGenerated: true },
        { ...this.emptyRow(), id: '', shouldOpenByDefault: false }
      )
    );
  }

  isRowValid(data: BeActivitiesAttributesModalRowData) {
    return (
      data.category &&
      data.activity_name &&
      !!data.activity_name.trim() &&
      this.isValidNumber(data.unit_cost) &&
      this.isValidNumber(data.unit_num)
    );
  }

  isSaveButtonDisabledAct(): boolean {
    if (
      (this.initialTableEmpty && !this.isActivitiesTabHasChanges()) ||
      (this.showNotesSection() ? !this.isThereAnyNotes.getValue() : false)
    ) {
      return true;
    }

    if (!this.isActivitiesTabHasChanges() && !this.hasExternalChanges) {
      return true;
    }

    if (this.ifAnyRowHasError()) {
      return true;
    }

    return Object.keys(this.invalidRows).length > 0;
  }

  isValidNumber(n: unknown): n is number {
    return Number.isFinite(parseFloat(<string>n));
  }

  onCellChanged(event: CellValueChangedEvent<BeActivitiesAttributesModalRowData>) {
    this.onCellEdit(event);
  }

  onCellEdit(
    params: Pick<
      CellEditingStoppedEvent<BeActivitiesAttributesModalRowData>,
      'data' | 'newValue' | 'node' | 'oldValue'
    >
  ) {
    if (params.newValue !== params.oldValue) {
      this.updateState(params.data);
      this.gridAPI.refreshCells({ rowNodes: [params.node] });
    }
  }

  onClickOutsideTable = (event: Event) => {
    const container = document.querySelector('.be-inline-container .ag-full-width-container');
    const target = event.target as HTMLElement;

    if (!container?.contains(target) && !target.classList.contains('ag-input-field-input')) {
      this.gridAPI.stopEditing();
    }
  };

  onCloseActivities(): void {
    this.closeTab.emit();
  }

  onGridReady(event: GridReadyEvent) {
    this.gridAPI = event.api;
    this.gridAPI.sizeColumnsToFit();

    document.addEventListener('click', this.onClickOutsideTable);
  }

  onSaveActivities(): void {
    this.save.emit({
      isCategoryListChanged: this.isCategoryListChanged.getValue(),
      notes: this.inAppDocumentState().notes,
      rows: this.getRowData(),
      supporting_document_s3_bucket_keys: this.inAppDocumentState().bucket_keys,
      temporaryCategories: this.categories,
    });
  }

  saveBtnTooltipAct(): string {
    if ((this.initialTableEmpty || !this.hasExternalChanges) && !this.isActivitiesTabHasChanges()) {
      return EMPTY_TABLE_TOOLTIP;
    }

    if (Object.keys(this.invalidRows).length > 0) {
      return this.saveButtonTooltip;
    }

    if (this.showNotesSection() && !this.isThereAnyNotes.getValue()) {
      return this.saveButtonTooltipForNotes;
    }

    if (this.ifAnyRowHasError()) {
      return PIVOT_ATTRIBUTE_ROW_ERROR;
    }

    return '';
  }

  scrollToNotes() {
    if (!this.showNotesSection()) {
      return;
    }

    if (
      !this.inAppDocumentState().notes &&
      !(Object.keys(this.invalidRows).length > 0) &&
      (this.isActivitiesTabHasChanges() || this.hasExternalChanges)
    ) {
      const elem = document.getElementById('notes');
      const auxInputElemId = elem?.children[0].id || '';
      const auxInputElem = document.getElementById(auxInputElemId);

      if (auxInputElem) {
        auxInputElem.focus();
        auxInputElem.blur();
      }
    }
  }

  setNotes(): void {
    this.isThereAnyNotes.next(!!this.inAppDocumentState().notes);
  }

  updateState(data?: BeActivitiesAttributesModalRowData) {
    if (data) {
      const isEmpty = this.isRowEmpty(data);
      let valid = this.isRowValid(data);
      const discountValid =
        data.activity_type === ActivityType.ACTIVITY_DISCOUNT && !data.isPercentage
          ? this.isValidNumber(data.unit_cost) && +data.unit_cost < 0
          : true;

      if (!discountValid) {
        valid = false;
      }

      if (valid) {
        this.validRows = {
          ...this.validRows,
          [data.id]: data,
        };
        delete this.invalidRows[data.id];
      } else {
        if (!isEmpty) {
          this.invalidRows = {
            ...this.invalidRows,
            [data.id]: data,
          };
        }

        delete this.validRows[data.id];
      }
    }

    const isThereAnyValidRow = !!Object.keys(this.validRows).length;
    const isThereAnyInvalidRow = !!Object.keys(this.invalidRows).length;
    const isThereAnyDeletedRow = !!Object.keys(this.deletedRows).length;
    const isThereAnyRow = this.gridAPI ? this.gridAPI.getDisplayedRowCount() > 0 : true;

    if (isThereAnyInvalidRow) {
      this.saveButtonDisabled.set(true);
      this.editButtonDisabled.set(true);
      this.saveButtonTooltip = ERROR_TABLE_TOOLTIP;
      this.editButtonTooltip = ERROR_TABLE_TOOLTIP;

      return;
    }

    this.saveButtonDisabled.set(false);
    this.editButtonDisabled.set(false);
    this.saveButtonTooltip = EMPTY_TABLE_TOOLTIP;
    this.editButtonTooltip = EMPTY_TABLE_TOOLTIP;

    if (!isThereAnyValidRow) {
      this.saveButtonDisabled.set(!isThereAnyDeletedRow);
      this.editButtonDisabled.set(!this.data?.editMode);
    }

    if (!isThereAnyRow) {
      this.editButtonDisabled.set(true);
    }
  }
}
